// Treatment detail — PI Platform outline v2 §3.14.
//
// Injuries and care gaps, on the Providers tab beside the records they
// come from.
//
// The care-gap half is the one that earns its place. The records
// already flag breaks of 45 days or more, but flagging was all that
// happened — the warning appeared and vanished with nowhere to record
// why. An unexplained gap is ammunition ("she waited seven weeks, so
// how hurt was she?"); an explained one is an asset. Same seven weeks,
// opposite effect, and the difference is whether somebody wrote the
// reason down while the client still remembered it.
//
// So unexplained gaps are stated as exposure rather than listed
// neutrally, and the explanation box is open by default — the cost of
// this feature is a phone call somebody has to be prompted to make.
//
// Injuries carry the pre-existing / aggravation pair, which decides how
// the injury is argued rather than just describing it. The card shows
// the resulting posture in words, because working it out from two
// checkboxes at the moment of writing a demand is exactly where it goes
// wrong.

import React, { useCallback, useEffect, useState } from "react";
import { C } from "../constants.js";
import { apiFetch } from "../api.js";
import { Spinner } from "../components/Spinner.jsx";

const SEVERITIES = ["", "minor", "moderate", "severe", "catastrophic"];
const EMPTY = {
  body_part: "", icd10_code: "", severity: "",
  is_pre_existing: false, is_aggravation: false, pre_existing_note: "",
};

export function TreatmentCard({ matterId, showNotification }) {
  const [injuries, setInjuries] = useState(null);
  const [gaps, setGaps] = useState(null);
  const [draft, setDraft] = useState(EMPTY);
  const [adding, setAdding] = useState(false);
  const [gapDraft, setGapDraft] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [i, g] = await Promise.all([
        apiFetch(`/api/matters/${matterId}/injuries`),
        apiFetch(`/api/matters/${matterId}/care-gaps`),
      ]);
      setInjuries(i.data || []);
      setGaps(g);
    } catch (e) {
      showNotification?.(`Treatment detail failed to load: ${e.message}`, "error");
      setInjuries([]);
      setGaps({ data: [], total: 0, unexplained: 0 });
    }
  }, [matterId, showNotification]);

  useEffect(() => { load(); }, [load]);

  const addInjury = async () => {
    if (!draft.body_part.trim()) {
      showNotification?.("Which body part?", "warning");
      return;
    }
    try {
      await apiFetch(`/api/matters/${matterId}/injuries`, {
        method: "POST", body: JSON.stringify(draft),
      });
      setDraft(EMPTY);
      setAdding(false);
      await load();
    } catch (e) {
      // The server explains why an aggravation needs the pre-existing
      // flag, or why a pre-existing condition needs a note. Those are
      // the useful sentences — pass them through.
      showNotification?.(e.message, "error");
    }
  };

  const detect = async () => {
    setBusy(true);
    try {
      const r = await apiFetch(`/api/matters/${matterId}/care-gaps/detect`,
        { method: "POST" });
      showNotification?.(
        r.detected === 0
          ? "No treatment gaps of 45 days or more in the records."
          : `${r.detected} gap(s) in the records — ${r.created} new, `
            + `${r.unchanged} already tracked.`, "success");
      await load();
    } catch (e) {
      showNotification?.(e.message, "error");
    }
    setBusy(false);
  };

  const explain = async (g, include) => {
    const text = (gapDraft[g.id] || "").trim();
    if (!text) {
      showNotification?.(
        "The explanation is the point — without one the gap stays an "
        + "unanswered question.", "warning");
      return;
    }
    try {
      await apiFetch(`/api/care-gaps/${g.id}/explain`, {
        method: "POST",
        body: JSON.stringify({ explanation: text, include_in_demand: include }),
      });
      setGapDraft((p) => ({ ...p, [g.id]: "" }));
      await load();
    } catch (e) {
      showNotification?.(e.message, "error");
    }
  };

  if (injuries === null || gaps === null) {
    return React.createElement("div", { style: cardStyle },
      React.createElement("div", {
        style: { display: "flex", alignItems: "center", gap: "8px" },
      },
        React.createElement(Spinner, { size: 16 }),
        React.createElement("span", {
          style: { fontSize: "13px", color: C.textMuted },
        }, "Loading treatment detail…")));
  }

  const unexplained = gaps.unexplained || 0;

  return React.createElement("div", { style: cardStyle },
    React.createElement("h2", { style: h2Style }, "Injuries and treatment gaps"),

    // ── Care gaps first: the thing with a deadline attached, in the
    // sense that a client's memory of last spring degrades weekly.
    React.createElement("div", { style: { marginTop: "14px" } },
      React.createElement("div", {
        style: { display: "flex", gap: "10px", alignItems: "baseline", flexWrap: "wrap" },
      },
        React.createElement("h3", { style: h3Style }, "Treatment gaps"),
        unexplained > 0 && React.createElement("span", { style: tagStyle },
          `${unexplained} unexplained`),
        React.createElement("div", { style: { flex: 1 } }),
        React.createElement("button", {
          onClick: detect, disabled: busy, style: quietBtn,
        }, busy ? "Checking…" : "Check the records"),
      ),

      gaps.total === 0
        ? React.createElement("p", {
            style: { fontSize: "13px", color: C.textMuted, margin: "7px 0 0" },
          }, "No gaps recorded. A break of 45 days or more between visits is "
           + "where an adjuster starts arguing the client had recovered.")
        : React.createElement("div", null,
            unexplained > 0 && React.createElement("p", {
              style: { fontSize: "13px", color: C.textDark, margin: "8px 0 0",
                       lineHeight: 1.55 },
            }, "An unexplained gap is the first thing an adjuster raises. "
             + "With a reason on file it goes in the demand instead, and "
             + "stops being a question. Client memory of why fades fast — "
             + "worth asking now."),

            ...gaps.data.map((g) => React.createElement("div", {
              key: g.id,
              style: { borderTop: `1px solid ${C.warmGray}`, padding: "12px 0",
                       marginTop: "10px",
                       opacity: g.status === "not_a_gap" ? 0.55 : 1 },
            },
              React.createElement("div", {
                style: { display: "flex", gap: "9px", alignItems: "baseline",
                         flexWrap: "wrap" },
              },
                React.createElement("span", {
                  style: { fontSize: "13px", fontWeight: 700, color: C.navy },
                }, `${g.days} days`),
                React.createElement("span", {
                  style: { fontSize: "12px", color: C.textMuted },
                }, `${g.gap_start} to ${g.gap_end}`),
                g.is_exposure && React.createElement("span", { style: tagStyle },
                  "Unexplained"),
                g.status === "explained" && !g.include_in_demand
                  && React.createElement("span", { style: tagStyle },
                       "Not in demand"),
              ),

              g.explanation && React.createElement("div", {
                style: { fontSize: "12px", color: C.textDark, marginTop: "6px",
                         background: C.warmWhite, borderRadius: "6px",
                         padding: "8px 10px" },
              }, g.explanation),

              g.is_exposure && React.createElement("div", { style: { marginTop: "8px" } },
                React.createElement("input", {
                  value: gapDraft[g.id] || "",
                  onChange: (e) => setGapDraft((p) => ({ ...p, [g.id]: e.target.value })),
                  placeholder: "Why was there a break? e.g. uninsured between jobs",
                  style: { ...inputStyle, width: "100%", marginBottom: "7px" },
                }),
                React.createElement("div", {
                  style: { display: "flex", gap: "7px", flexWrap: "wrap" },
                },
                  React.createElement("button", {
                    onClick: () => explain(g, true), style: primaryBtn,
                  }, "Record and use in demand"),
                  React.createElement("button", {
                    onClick: () => explain(g, false), style: quietBtn,
                  }, "Record only"),
                ),
              ),
            )),
          ),
    ),

    // ── Injuries ────────────────────────────────────────────────
    React.createElement("div", { style: { marginTop: "24px" } },
      React.createElement("div", {
        style: { display: "flex", gap: "10px", alignItems: "baseline", flexWrap: "wrap" },
      },
        React.createElement("h3", { style: h3Style }, "Injuries claimed"),
        React.createElement("div", { style: { flex: 1 } }),
        React.createElement("button", {
          onClick: () => setAdding((v) => !v), style: quietBtn,
        }, "Add an injury"),
      ),

      adding && React.createElement("div", {
        style: { marginTop: "11px", padding: "13px 15px", background: C.warmWhite,
                 borderRadius: "8px" },
      },
        React.createElement("div", {
          style: { display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "8px" },
        },
          React.createElement("input", {
            value: draft.body_part,
            onChange: (e) => setDraft((p) => ({ ...p, body_part: e.target.value })),
            placeholder: "Body part", style: { ...inputStyle, flex: "2 1 160px" },
          }),
          React.createElement("input", {
            value: draft.icd10_code,
            onChange: (e) => setDraft((p) => ({ ...p, icd10_code: e.target.value })),
            placeholder: "ICD-10", style: { ...inputStyle, flex: "1 1 100px" },
          }),
          React.createElement("select", {
            value: draft.severity,
            onChange: (e) => setDraft((p) => ({ ...p, severity: e.target.value })),
            style: { ...inputStyle, flex: "1 1 120px" },
          }, ...SEVERITIES.map((s) =>
            React.createElement("option", { key: s, value: s },
              s || "Severity…"))),
        ),
        React.createElement("label", {
          style: { display: "flex", gap: "7px", alignItems: "center",
                   fontSize: "12px", color: C.textDark, marginBottom: "6px" },
        },
          React.createElement("input", {
            type: "checkbox", checked: draft.is_pre_existing,
            onChange: (e) => setDraft((p) => ({ ...p, is_pre_existing: e.target.checked })),
          }),
          "This condition pre-existed the incident"),
        draft.is_pre_existing && React.createElement("div", null,
          React.createElement("label", {
            style: { display: "flex", gap: "7px", alignItems: "center",
                     fontSize: "12px", color: C.textDark, marginBottom: "6px" },
          },
            React.createElement("input", {
              type: "checkbox", checked: draft.is_aggravation,
              onChange: (e) => setDraft((p) => ({ ...p, is_aggravation: e.target.checked })),
            }),
            "The incident made it worse (claimed as an aggravation)"),
          React.createElement("input", {
            value: draft.pre_existing_note,
            onChange: (e) => setDraft((p) => ({ ...p, pre_existing_note: e.target.value })),
            placeholder: "What it was, and how the incident changed it",
            style: { ...inputStyle, width: "100%", marginBottom: "8px" },
          }),
        ),
        React.createElement("button", { onClick: addInjury, style: primaryBtn }, "Add"),
      ),

      injuries.length === 0
        ? React.createElement("p", {
            style: { fontSize: "13px", color: C.textMuted, margin: "8px 0 0" },
          }, "Nothing recorded yet. This is what the demand describes and "
           + "what damages attach to.")
        : React.createElement("div", null,
            ...injuries.map((i) => React.createElement("div", {
              key: i.id,
              style: { borderTop: `1px solid ${C.warmGray}`, padding: "11px 0",
                       marginTop: "9px" },
            },
              React.createElement("div", {
                style: { display: "flex", gap: "9px", alignItems: "baseline",
                         flexWrap: "wrap" },
              },
                React.createElement("span", {
                  style: { fontSize: "13px", fontWeight: 600, color: C.navy },
                }, i.body_part),
                i.icd10_code && React.createElement("span", {
                  style: { fontSize: "11px", color: C.textMuted,
                           fontFamily: "ui-monospace, monospace" },
                }, i.icd10_code),
                i.severity && React.createElement("span", { style: tagStyle }, i.severity),
                i.needs_review && React.createElement("span", { style: tagStyle },
                  "Check posture"),
              ),
              React.createElement("div", {
                style: { fontSize: "12px", color: C.textMuted, marginTop: "3px" },
              }, i.claim_posture),
              i.pre_existing_note && React.createElement("div", {
                style: { fontSize: "12px", color: C.textDark, marginTop: "5px" },
              }, i.pre_existing_note),
            )),
          ),
    ),
  );
}

const cardStyle = {
  background: "#fff", borderRadius: "12px", padding: "20px 24px",
  border: "1px solid #E8E3DC", marginBottom: "20px",
};
const h2Style = {
  fontSize: "13px", color: "#6B6660", textTransform: "uppercase",
  letterSpacing: "0.08em", fontWeight: 600, margin: 0,
};
const h3Style = {
  fontSize: "12px", color: "#1B2A4A", fontWeight: 700, margin: 0,
};
const inputStyle = {
  padding: "7px 10px", borderRadius: "6px", border: "1px solid #E8E3DC",
  fontSize: "12px", fontFamily: "inherit", background: "#fff",
  boxSizing: "border-box", minWidth: 0,
};
const quietBtn = {
  border: "1px solid #E8E3DC", background: "#fff", borderRadius: "6px",
  padding: "7px 13px", fontSize: "12px", fontWeight: 600,
  color: "#1B2A4A", cursor: "pointer", flexShrink: 0,
};
const primaryBtn = {
  border: "1px solid #1B2A4A", background: "#1B2A4A", borderRadius: "6px",
  padding: "7px 14px", fontSize: "12px", fontWeight: 600,
  color: "#fff", cursor: "pointer", flexShrink: 0,
};
const tagStyle = {
  fontSize: "10px", fontWeight: 700, textTransform: "uppercase",
  letterSpacing: "0.06em", color: "#6B6660",
  border: "1px solid #E8E3DC", borderRadius: "4px", padding: "2px 7px",
};
